// pages/TicketManagement/components/TicketFormModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "hooks/useSession";
import { useRolePermissions } from "./useRolePermissions";
import { TicketManagement } from "./ticketManagement";
import { fetchWrapper } from "utils/apiFetch";

/* ─────────────────────────────────────────────
   Inline styles (no external CSS dependency)
   Matches Mokshiq modern light fintech theme
───────────────────────────────────────────── */
const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 1055,
    background: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
    animation: "fadeIn .18s ease",
  },
  modal: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "720px",
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 30px -10px rgba(0, 0, 0, 0.15), 0 10px 15px -5px rgba(0, 0, 0, 0.08)",
    animation: "slideUp .22s cubic-bezier(.34,1.56,.64,1)",
    overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "16px 20px 14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "18px", fontWeight: 700, color: "#0f172a",
    fontFamily: "'DM Sans', sans-serif", margin: 0,
  },
  headerSub: {
    fontSize: "12px", color: "#64748b", marginTop: "3px",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  closeBtn: {
    background: "#f1f5f9", border: "1px solid #e2e8f0", cursor: "pointer",
    color: "#64748b", fontSize: "16px", lineHeight: 1,
    padding: "6px 10px", borderRadius: "8px",
    transition: "all .15s",
  },
  body: {
    overflowY: "auto", padding: "16px 18px", flex: 1, background: "#ffffff",
  },
  section: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "14px 16px",
    marginBottom: "14px",
  },
  sectionLabel: {
    fontSize: "11px", fontWeight: 700, letterSpacing: "0.8px",
    textTransform: "uppercase", color: "#0d9488",
    marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px",
  },
  row: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px", marginBottom: "12px" },
  rowFull: { display: "grid", gridTemplateColumns: "1fr", gap: "12px", marginBottom: "12px" },
  label: {
    fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px",
    textTransform: "uppercase", color: "#475569",
    display: "block", marginBottom: "5px",
  },
  required: { color: "#dc2626", marginLeft: "3px" },
  input: {
    width: "100%", background: "#ffffff",
    border: "1px solid #cbd5e1", borderRadius: "8px",
    color: "#0f172a", fontSize: "13.5px",
    padding: "9px 12px", outline: "none",
    transition: "border-color .15s, box-shadow .15s",
    boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif",
  },
  inputFocus: { borderColor: "#0d9488", boxShadow: "0 0 0 3px rgba(13,148,136,.15)" },
  textarea: {
    width: "100%", background: "#ffffff",
    border: "1px solid #cbd5e1", borderRadius: "8px",
    color: "#0f172a", fontSize: "13.5px",
    padding: "9px 12px", outline: "none", resize: "vertical", minHeight: "90px",
    transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
  },
  select: {
    width: "100%", background: "#ffffff",
    border: "1px solid #cbd5e1", borderRadius: "8px",
    color: "#0f172a", fontSize: "13.5px",
    padding: "9px 12px", outline: "none", cursor: "pointer",
    transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
  },
  errorText: { fontSize: "11px", color: "#dc2626", marginTop: "4px" },
  footer: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 24px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0,
  },
  cancelBtn: {
    background: "#ffffff", border: "1px solid #cbd5e1",
    borderRadius: "8px", color: "#475569",
    padding: "9px 20px", fontSize: "13.5px", fontWeight: 600,
    cursor: "pointer", transition: "all .15s",
    fontFamily: "'DM Sans', sans-serif",
  },
  submitBtn: {
    background: "#0d9488", border: "1px solid #0d9488", borderRadius: "8px",
    color: "#ffffff", padding: "9px 24px", fontSize: "13.5px", fontWeight: 700,
    cursor: "pointer", transition: "all .15s",
    display: "flex", alignItems: "center", gap: "7px",
    fontFamily: "'DM Sans', sans-serif",
    boxShadow: "0 1px 2px rgba(13,148,136,0.2)",
  },
  badge: (color, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: bg, color, border: border ? `1px solid ${border}` : "none",
    fontSize: "11px", fontWeight: 700,
    padding: "3px 10px", borderRadius: "20px",
  }),
  internalNote: {
    background: "#fffbeb", border: "1px solid #fde68a",
    borderRadius: "8px", padding: "12px 14px",
    fontSize: "12.5px", color: "#92400e",
    display: "flex", alignItems: "flex-start", gap: "8px",
  },
  charCount: { fontSize: "11px", color: "#94a3b8", textAlign: "right", marginTop: "3px" },
};

/* ── small styled subcomponents ── */
const Field = ({ label, required, error, children, style }) => (
  <div style={style}>
    <label style={S.label}>{label}{required && <span style={S.required}>*</span>}</label>
    {children}
    {error && <div style={S.errorText}>⚠ {error}</div>}
  </div>
);

const FocusInput = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{ ...style, ...(focused ? S.inputFocus : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const FocusSelect = ({ children, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{ ...S.select, ...(focused ? S.inputFocus : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const EMPTY_FORM = {
  title:            "",
  description:      "",
  ticket_type:      "support",
  priority:         "medium",
  status:           "open",
  amount_involved:  "",
  currency:         "INR",
  reference_number: "",
  assigned_to:      "",
  due_date:         "",
  tags:             "",
  category:         "",
  remark:           "",
};

const TicketFormModal = ({ show, onClose, mode = "add", userData = null, onSuccess }) => {
  const { userid } = useSession();
  const { can, roleName, isAdmin } = useRolePermissions(TicketManagement);

  const [form, setForm]     = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [agentList, setAgentList] = useState([]);
  const [descLen, setDescLen] = useState(0);

  /* ── populate form in edit mode ── */
  useEffect(() => {
    if (!show) return;
    setApiError("");
    setErrors({});
    if (mode === "edit" && userData) {
      const filled = { ...EMPTY_FORM, ...userData };
      setForm(filled);
      setDescLen(filled.description?.length || 0);
    } else {
      setForm(EMPTY_FORM);
      setDescLen(0);
    }
  }, [show, mode, userData]);

  /* ── fetch assignable agents (staff users) when admin/wl opens modal ── */
  useEffect(() => {
    if (!show || !can.assign) return;
    const fd = new FormData();
    fd.append("action", "GET_AGENTS");
    fd.append("userid", userid);
    fetch(TicketManagement.apiUrl, { method: "POST", body: fd })
      .then(r => r.json())
      .then(d => setAgentList(d?.data || []))
      .catch(() => setAgentList([]));
  }, [show, can.assign, userid]);

  /* ── field change ── */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === "description") setDescLen(value.length);
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  }, [errors]);

  /* ── validation ── */
  const validate = () => {
    const e = {};
    if (!form.title.trim())       e.title       = "Title is required";
    if (!form.description.trim()) e.description  = "Description is required";
    if (!form.ticket_type)        e.ticket_type  = "Please select a type";
    if (!form.priority)           e.priority     = "Please select priority";
    if (!form.status)             e.status       = "Please select status";
    if (form.amount_involved && isNaN(Number(form.amount_involved)))
                                  e.amount_involved = "Must be a valid number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setApiError("");
    try {
      const fd = new FormData();
      fd.append("action", mode === "edit" ? "UPDATE" : "CREATE");
      fd.append("userid", userid);
      fd.append("role",   roleName);
      const ticketPk = userData?.id || userData?.ticket_id;
      if (mode === "edit" && ticketPk) {
        fd.append("id", ticketPk);
        fd.append("ticket_id", ticketPk);
      }
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));

      const data  = await fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd });

      const isSuccess =
        data?.status === "success" ||
        data?.status === true ||
        data?.success === true ||
        data?.status === 1 ||
        (data?.status && data?.status !== "error");

      if (isSuccess) {
        onSuccess?.();
      } else {
        setApiError(data?.message || data?.data || "Something went wrong. Please try again.");
      }
    } catch {
      setApiError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const isEdit = mode === "edit";
  const config = TicketManagement;

  return (
    <>
      {/* ── keyframe injection ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        .tm-input:focus  { border-color:#0d9488!important; box-shadow:0 0 0 3px rgba(13,148,136,.15)!important; }
        .tm-cancel:hover { border-color:#94a3b8!important; color:#0f172a!important; background:#f1f5f9!important; }
        .tm-submit:hover { opacity:.92; transform:translateY(-1px); }
        .tm-close:hover  { color:#0f172a!important; background:#e2e8f0!important; }
      `}</style>

      <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={S.modal}>

          {/* ── Header ── */}
          <div style={S.header}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "22px" }}>{isEdit ? "✏️" : "🎫"}</span>
                <h2 style={S.headerTitle}>
                  {isEdit ? "Edit Ticket" : "Create New Ticket"}
                </h2>
                {isEdit && userData?.ticket_id && (
                  <span style={S.badge("#0d9488", "#f0fdfa", "#ccfbf1")}>
                    {userData.ticket_id}
                  </span>
                )}
              </div>
              <div style={S.headerSub}>
                Role: <strong style={{ color: "#0d9488" }}>{roleName.replace(/_/g, " ").toUpperCase()}</strong>
                &nbsp;·&nbsp; All fields marked <span style={{ color: "#dc2626" }}>*</span> are required
              </div>
            </div>
            <button style={S.closeBtn} className="tm-close" onClick={onClose}>✕</button>
          </div>

          {/* ── Body ── */}
          <div style={S.body}>

            {/* API Error Banner */}
            {apiError && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "8px", padding: "12px 16px", marginBottom: "16px",
                fontSize: "13px", color: "#dc2626", display: "flex", alignItems: "center", gap: "8px"
              }}>
                <span>⚠</span> {apiError}
              </div>
            )}

            {/* ── Section 1: Core Info ── */}
            <div style={S.section}>
              <div style={S.sectionLabel}>🎫 Core Information</div>

              <div style={S.rowFull}>
                <Field label="Subject / Title" required error={errors.title}>
                  <FocusInput
                    name="title" value={form.title} onChange={handleChange}
                    placeholder="Brief description of the issue..." style={S.input}
                    disabled={isEdit && !can.edit}
                  />
                </Field>
              </div>

              <div style={S.row}>
                <Field label="Issue Type" required error={errors.ticket_type}>
                  <FocusSelect name="ticket_type" value={form.ticket_type} onChange={handleChange}
                    disabled={isEdit && !can.edit}>
                    <option value="">— Select Type —</option>
                    {config.ticketTypeOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </FocusSelect>
                </Field>

                <Field label="Priority" required error={errors.priority}>
                  <FocusSelect name="priority" value={form.priority} onChange={handleChange}
                    disabled={isEdit && !can.edit}>
                    {config.priorityOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </FocusSelect>
                </Field>
              </div>

              {/* Status — only admins can change in edit mode */}
              {(isAdmin || !isEdit) && (
                <div style={{ ...S.row, gridTemplateColumns: "1fr 1fr" }}>
                  <Field label="Status" required error={errors.status}>
                    <FocusSelect name="status" value={form.status} onChange={handleChange}>
                      {config.statusOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </FocusSelect>
                  </Field>
                  <Field label="Category">
                    <FocusInput name="category" value={form.category} onChange={handleChange}
                      placeholder="e.g. AEPS, DMT, Recharge..." style={S.input} />
                  </Field>
                </div>
              )}

              <div style={S.rowFull}>
                <Field label="Description" required error={errors.description}>
                  <textarea
                    name="description" value={form.description} onChange={handleChange}
                    placeholder="Describe the issue in detail — steps to reproduce, error messages, affected transactions..."
                    style={{ ...S.textarea, ...(errors.description ? { borderColor: "#ef4444" } : {}) }}
                    className="tm-input"
                    disabled={isEdit && !can.edit}
                    maxLength={1000}
                  />
                  <div style={S.charCount}>{descLen} / 1000</div>
                </Field>
              </div>
            </div>

            {/* ── Section 2: Financial Details ── */}
            <div style={S.section}>
              <div style={S.sectionLabel}>💰 Financial Details <span style={{ color: "#525d72", fontSize: "9px" }}>(OPTIONAL)</span></div>

              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: "14px", marginBottom: "14px" }}>
                <Field label="Amount Involved" error={errors.amount_involved}>
                  <FocusInput name="amount_involved" value={form.amount_involved} onChange={handleChange}
                    placeholder="0.00" type="number" step="0.01" style={S.input}
                    disabled={isEdit && !can.edit} />
                </Field>
                <Field label="Currency">
                  <FocusSelect name="currency" value={form.currency} onChange={handleChange}
                    disabled={isEdit && !can.edit}>
                    {["INR", "USD", "EUR", "GBP", "AED"].map(c => <option key={c}>{c}</option>)}
                  </FocusSelect>
                </Field>
                <Field label="Reference / TXN No.">
                  <FocusInput name="reference_number" value={form.reference_number} onChange={handleChange}
                    placeholder="TXN-XXXX or order ref..." style={S.input}
                    disabled={isEdit && !can.edit} />
                </Field>
              </div>
            </div>

            {/* ── Section 3: Assignment & SLA (role-gated) ── */}
            {can.assign && (
              <div style={S.section}>
                <div style={S.sectionLabel}>👤 Assignment & SLA</div>

                <div style={S.row}>
                  <Field label="Assign To (Agent)">
                    <FocusSelect name="assigned_to" value={form.assigned_to} onChange={handleChange}>
                      <option value="">— Unassigned —</option>
                      {agentList.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.first_name} {a.last_name} ({a.username})
                        </option>
                      ))}
                    </FocusSelect>
                  </Field>

                  <Field label="Due Date / SLA">
                    <FocusInput name="due_date" value={form.due_date} onChange={handleChange}
                      type="datetime-local" style={S.input} />
                  </Field>
                </div>
              </div>
            )}

            {/* ── Section 4: Tags & Remark ── */}
            <div style={S.section}>
              <div style={S.sectionLabel}>🏷 Tags & Notes</div>

              <div style={S.row}>
                <Field label="Tags" style={{}}>
                  <FocusInput name="tags" value={form.tags} onChange={handleChange}
                    placeholder="fraud, urgent, escalated (comma-separated)" style={S.input}
                    disabled={isEdit && !can.edit} />
                </Field>
                <Field label="Internal Remark">
                  <FocusInput name="remark" value={form.remark} onChange={handleChange}
                    placeholder="Internal note for agents..." style={S.input}
                    disabled={isEdit && !can.edit} />
                </Field>
              </div>
            </div>

            {/* ── Role Notice for restricted users ── */}
            {isEdit && !can.edit && (
              <div style={S.internalNote}>
                <span style={{ fontSize: "16px" }}>🔒</span>
                <span>
                  Your role (<strong>{roleName.replace(/_/g, " ")}</strong>) has view-only access.
                  Contact your admin to modify this ticket.
                </span>
              </div>
            )}

          </div>

          {/* ── Footer ── */}
          <div style={S.footer}>
            <div style={{ fontSize: "12px", color: "#525d72" }}>
              {isEdit ? "Editing ticket" : "New ticket will be auto-assigned an ID"}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={S.cancelBtn} className="tm-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              {(!isEdit || can.edit) && (
                <button style={S.submitBtn} className="tm-submit" onClick={handleSubmit} disabled={loading}>
                  {loading
                    ? <><span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> Saving...</>
                    : <>{isEdit ? "💾 Save Changes" : "🎫 Create Ticket"}</>
                  }
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default TicketFormModal;